import {
  createContext, useCallback, useContext, useState, type ReactNode,
} from 'react';
import type { TaskSummary } from '@todo/shared';
import { ApiError } from '../api/client';
import { taskApi } from '../api/task.api';
import { TaskDetailPanel } from '../components/TaskDetailPanel';

interface TaskViewerContextValue {
  /** Open the detail panel for a task by id, from anywhere - it floats
   *  above whatever page is currently showing. */
  openTask: (taskId: string) => void;
  /** The task currently showing in the drawer, or null - lets a list (the
   *  notification bell, the Notifications page) highlight which of its own
   *  rows is the one open right now. */
  openTaskId: string | null;
}

const TaskViewerContext = createContext<TaskViewerContextValue | null>(null);

/**
 * Mounted once at the app shell (see AppLayout), so anything that only
 * knows a task's ID - the notification bell, the Notifications page - can
 * jump straight to that task's detail panel without needing its own copy
 * of that task's list to open a row from.
 */
export function TaskViewerProvider({ children }: { children: ReactNode }) {
  const [task, setTask] = useState<TaskSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  const openTask = useCallback((taskId: string) => {
    setError(null);
    void taskApi.byId(taskId)
      .then(setTask)
      .catch((err) => {
        setError(err instanceof ApiError ? err.message : 'Could not open that task');
      });
  }, []);

  return (
    <TaskViewerContext.Provider value={{ openTask, openTaskId: task?.id ?? null }}>
      {children}

      {task && (
        <TaskDetailPanel
          task={task}
          onClose={() => setTask(null)}
          onChanged={setTask}
        />
      )}

      {error && (
        <div
          role="alert"
          className="fixed bottom-4 right-4 z-50 flex items-center gap-3 rounded-lg bg-red-600 px-4 py-2.5 text-sm text-white shadow-lg"
        >
          <span>{error}</span>
          <button
            type="button"
            onClick={() => setError(null)}
            className="font-semibold underline underline-offset-2"
          >
            Dismiss
          </button>
        </div>
      )}
    </TaskViewerContext.Provider>
  );
}

/** `openTask(id)` from anywhere inside the app shell - see TaskViewerProvider. */
export function useTaskViewer(): TaskViewerContextValue {
  const ctx = useContext(TaskViewerContext);
  if (!ctx) throw new Error('useTaskViewer must be used within TaskViewerProvider');
  return ctx;
}
